import type { JSX } from "react";
import { createRoot } from "react-dom/client";
import { isBrowser, isBrowserNotSupported, isMobileOnly } from "@remhealth/ai";
import { ErrorBoundary, Suspense } from "@remhealth/core";
import { lazy } from "@remhealth/ui";
import DiagnosticsPage from "./diagnostics";

const EMBED_APPS = {
  "quality-coach": lazy(() => import("./embed/qualityCoachApp")),
  settings: lazy(() => import("./embed/settingsApp")),
} as const;

type EmbedAppName = keyof typeof EMBED_APPS;

function getEmbedApp(): EmbedAppName | null {
  const pathname = window.location.pathname;
  const match = pathname.match(/^\/_embed\/(.+?)(?:\/|$)/);

  if (match) {
    const name = match[1] as EmbedAppName;
    if (name in EMBED_APPS) {
      return name;
    }
  }

  return null;
}

const BellsApp = lazy(() => import("./bellsApp"));

let rootElement: JSX.Element | undefined;

const embedApp = getEmbedApp();

if (embedApp) {
  const EmbedRoot = EMBED_APPS[embedApp];
  rootElement = (
    <ErrorBoundary>
      <Suspense minimal>
        <EmbedRoot />
      </Suspense>
    </ErrorBoundary>
  );
} else if (isMobileOnly) {
  location.href = "./no-mobile.html";
} else if (isBrowser && isBrowserNotSupported()) {
  location.href = "./unsupported.html";
} else if (window.location.pathname === "/diagnostics") {
  rootElement = <DiagnosticsPage />;
} else {
  rootElement = (
    <Suspense minimal>
      <BellsApp />
    </Suspense>
  );
}

if (rootElement) {
  const root = createRoot(document.getElementById("root")!);
  root.render(rootElement);
}
